import {Component, OnDestroy, OnInit, Inject, ChangeDetectionStrategy} from '@angular/core';
import {MatDialog, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {IrbAppEvents} from '@irb/app.events';
import {AppService} from '@irb/app.service';
import {IrbOrdersEvents} from '@irb/irb-orders/IrbOrdersEvents';
import {InventoryService} from '../inventory/inventory.service';
import {UtilityService} from '@irb-common/services/utility.service';
import {IAlertDialogData} from "@irb/app.model";

@Component({ standalone: false,
    selector: 'irb-import-orders',
    templateUrl: './import-orders.component.html',
    styleUrls: ['./import-orders.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ImportOrdersComponent implements OnInit, OnDestroy {
    hasFile: boolean = false;
    isValidFile: boolean = false;
    invalidFileErrorHeader: string;
    invalidFileErrorMessage: string;
    errorMessage: string;

    uploadedFileName: string;
    disableContinue: boolean;
    formData: any;
    response: any;
    file: any;

    progressTimer: any = null;
    updateStatusCount = 0;
    isUiTest = false;

    constructor(
        public dialog: MatDialog,
        private as: AppService,
        private readonly pfs: InventoryService,
        private util: UtilityService,
        @Inject(MAT_DIALOG_DATA) public mdd: any,
    ) {
        this.disableContinue = true;
    }

    ngOnInit() {
    }


    fileUploaded(fileInputEvent: any) {
        this.file = fileInputEvent.target.files[0];
        this.uploadedFileName = this.file.name;

        this.hasFile = this.isValidFile = true;
        if (!this.file.name.endsWith('.csv')) {
            this.isValidFile = false;
            this.invalidFileErrorHeader = "Invalid file format.";
            this.invalidFileErrorMessage = "Extension type must be \".csv\"";
        } else {
            if (this.as.maxFileUploadSizeExceeded(this.file)) {
                this.triggerMaxFileUploadSizeExceededError();
            } else {
                this.disableContinue = false;
                this.formData = new FormData();
                this.formData.append('ImportOrders0', fileInputEvent.target.files[0], fileInputEvent.target.files[0].name);
            }
        } 
    }

    private triggerMaxFileUploadSizeExceededError() {
        this.isValidFile = false;
        this.invalidFileErrorHeader = "Invalid file size.";
        this.invalidFileErrorMessage = "File size exceeds 50MB limit.";
    }

    validateImportOrders() {
        this.pfs.validateImportDataAsync(this.formData).subscribe({
            next: (data: any) => {
                if (this.progressTimer) clearTimeout(this.progressTimer);
                if (data.responseStatus === "SUCCESS") {
                    this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
                    this.orderImportValidateCheckIntervalStart(data.responseUtilityMap.statusId, 'VALIDATE_ORDERS', null);
                } else {
                    this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                        severity: 'error',
                        summary: 'Import Orders',
                        detail: " An error occurred processing your request."
                    });
                }
            }, error: () => {}
        });
    }

    proceedImportOrders(res: any) {
        if (res.includes('Max upload size exceeded')) {
            this.triggerMaxFileUploadSizeExceededError();
        } else {
            let response = JSON.parse(res.substring(res.indexOf('<textarea>') + 10, res.indexOf('</textarea>')));

            if(response.status && response.status === "fail") {
                if(response.message && response.message === "1000OrdersAndAboveMsg"){
                    this.errorMessage = "You cannot import more than 1,000 orders at one time. Please reduce the number of orders on the file before re-importing.";
                } else {
                    if(response.message) {
                        this.errorMessage = response.message;
                    } else {
                        this.isValidFile = false;
                    }
                }
                return;
            }

            this.cancel();
            const isValid = (response.validRecords && response.validRecords.length > 0);
            const isInValid = (response.invalidRecords && response.invalidRecords.length > 0);
            const isActiveOrders = (response.activeOrderAcctNumSet && response.activeOrderAcctNumSet.length > 0);
            response.currentTab = this.mdd.currentTab;

            if(isInValid || isActiveOrders || response.isDuplicateRecordExists) {
                this.as.fireEvent(IrbOrdersEvents.SHOW_IMPORT_ORDERS_RESULT, response);
            } else if(isValid) {
                if(response.nonInventoriedSecurities && response.nonInventoriedSecurities.length > 0){
                   this.saveTempSecurities(response);
                } else {
                    this.insertImportOrders(response.validRecords);
                }                
            }
        }
    }

    saveTempSecurities(response: any) {
        this.pfs.saveTempSecuritiesAsync(response.nonInventoriedSecurities).subscribe((data: any) => {
            if (this.progressTimer) clearTimeout(this.progressTimer);
            if(data.responseStatus === "SUCCESS") {
                this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
                this.orderImportSaveTempSecCheckIntervalStart(data.responseUtilityMap.statusId, 'SAVE_TEMP_SECURITIES', response.validRecords);
            } else {
                this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                    severity: 'error',
                    summary: 'Import Orders',
                    detail: " An error occurred processing your request."
                });
            }
        }, () => {
            this.cancel();
        });
    }

    insertImportOrders(validRecords: any) {
        this.pfs.insertImportedOrdersAsync(validRecords, this.mdd.currentTab).subscribe((data: any) => {
            if (this.progressTimer) clearTimeout(this.progressTimer);
            if (data.responseStatus === "SUCCESS") {
                this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
                this.orderImportInsertCheckIntervalStart(data.responseUtilityMap.statusId, 'INSERT_ORDERS', null);
            } else {
                this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                    severity: 'error',
                    summary: 'Import Orders',
                    detail: " An error occurred processing your request."
                });
            }
        }, () => {
            this.cancel();
        });
    }

    validateImportData() {
        this.pfs.validateImportData(this.formData).subscribe({
            next: (res: string) => {
                if (res.includes('Max upload size exceeded')) {
                    this.triggerMaxFileUploadSizeExceededError();
                } else {
                    let response = JSON.parse(res.substring(res.indexOf('<textarea>') + 10, res.indexOf('</textarea>')));

                    if(response.status && response.status === "fail") {
                        if(response.message && response.message === "1000OrdersAndAboveMsg"){
                            this.errorMessage = "You cannot import more than 1,000 orders at one time. Please reduce the number of orders on the file before re-importing.";
                        } else {
                            if(response.message) {
                                this.errorMessage = response.message;
                            } else {
                                this.isValidFile = false;
                            }
                        }
                        return;
                    }

                    this.cancel();
                    const isValid = (response.validRecords && response.validRecords.length > 0);
                    const isInValid = (response.invalidRecords && response.invalidRecords.length > 0);
                    const isActiveOrders = (response.activeOrderAcctNumSet && response.activeOrderAcctNumSet.length > 0);
                    response.currentTab = this.mdd.currentTab;

                    if(isInValid || isActiveOrders || response.isDuplicateRecordExists) {
                        this.as.fireEvent(IrbOrdersEvents.SHOW_IMPORT_ORDERS_RESULT, response);
                    } else if(isValid) {
                        if(response.nonInventoriedSecurities && response.nonInventoriedSecurities.length > 0){
                            this.pfs.saveTempSecurities(response.nonInventoriedSecurities).subscribe((data: any) => {
                                if(data.responseStatus === "SUCCESS") {
                                } else {
                                    this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                                        severity: 'error',
                                        summary: 'Import Orders',
                                        detail: " An error occurred processing your request."
                                    });
                                }
                            });
                        }

                        this.pfs.insertImportedOrdersAsync(response.validRecords, this.mdd.currentTab).subscribe((data: any) => {
                            if (this.progressTimer) clearTimeout(this.progressTimer);
                            this.cancel();
                            if (data.responseStatus === "SUCCESS") {
                                this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
                                this.actionStatusCheckIntervalStart(data.responseUtilityMap.statusId, '', null);
                            } else {
                                this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                                    severity: 'error',
                                    summary: 'Import Orders',
                                    detail: " An error occurred processing your request."
                                });
                            }
                        }, () => {
                            this.cancel();
                        });
                    }
                }
            }, error: () => {}
        });
    }

    cancel() {
        this.dialog.closeAll();
    }

    updateProgressBarStatus(response: any, type: string, validRecords: any) {
        let timeout = (this.updateStatusCount > 10) ? 8000 : (this.updateStatusCount > 2) ? 3000 : 1500;
        if (this.isUiTest)
            timeout = 0;
        this.updateStatusCount++;
        this.progressTimer = setTimeout(() => {
            if (type === 'VALIDATE_ORDERS') {
                this.orderImportValidateCheckIntervalStart(response.statusId, type, validRecords);
            } 
            if (type === 'SAVE_TEMP_SECURITIES') {
                this.orderImportSaveTempSecCheckIntervalStart(response.statusId, type, validRecords);
            } 
            if (type === 'INSERT_ORDERS') {
                this.orderImportInsertCheckIntervalStart(response.statusId, type, validRecords);
            }
        }, timeout);
    }

    orderImportValidateCheckIntervalStart(statusId: any, type: string, validRecords: any) {
        this.pfs.validateImportOrdersAsyncStatus(statusId).subscribe((statusResponse: any) => {
            if (statusResponse.responseStatus === "SUCCESS") {
                const response = statusResponse?.responseUtilityMap;
                const reqStatus = response?.reqStatus?.trim().toUpperCase();
                if (reqStatus === 'FAILED') {
                    this.actionStatusCheckFailed();
                }

                if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
                    this.updateProgressBarStatus(response, type, validRecords);
                }
                if (reqStatus === 'COMPLETED') {
                    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                    if (this.progressTimer) clearTimeout(this.progressTimer);
                    this.proceedImportOrders(statusResponse.responseData);
                }
            } else {
                this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                if (this.progressTimer) clearTimeout(this.progressTimer);
            }
        }, () => {
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
            if (this.progressTimer) clearTimeout(this.progressTimer);
        });
    }

    orderImportSaveTempSecCheckIntervalStart(statusId: any, type: string, validRecords: any) {
        this.pfs.saveTempSecAsyncStatus(statusId).subscribe((statusResponse: any) => {
            if (statusResponse.responseStatus === "SUCCESS") {
                const response = statusResponse?.responseData;
                const reqStatus = response?.reqStatus?.trim().toUpperCase();
                if (reqStatus === 'FAILED') {
                    this.actionStatusCheckFailed();
                }

                if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
                    this.updateProgressBarStatus(response, type, validRecords);
                }
                if (reqStatus === 'COMPLETED') {
                    this.insertImportOrders(validRecords);
                }
            } else {
                this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                if (this.progressTimer) clearTimeout(this.progressTimer);
            }
        }, () => {
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
            if (this.progressTimer) clearTimeout(this.progressTimer);
        });
    }

    orderImportInsertCheckIntervalStart(statusId: any, type: string, validRecords: any) {
        this.pfs.insertImportOrdersAsyncStatus(statusId).subscribe((statusResponse: any) => {
            if (statusResponse.responseStatus === "SUCCESS") {
                const response = statusResponse.responseData;
                const reqStatus = response.reqStatus.trim().toUpperCase();
                if (reqStatus === 'FAILED') {
                    if (response.failMsg && response.failMsg.toUpperCase() === 'STALEDATAEXCEPTION')
                        this.showStaleDataDialog();
                    else
                        this.actionStatusCheckFailed();

                    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                }

                if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
                    this.updateProgressBarStatus(response, type, validRecords);
                }
                if (reqStatus === 'COMPLETED') {
                    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                    if (this.progressTimer) clearTimeout(this.progressTimer);
                    this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                        severity: 'success',
                        summary: 'Import Orders',
                        detail: response.successRecCount + " orders imported/updated."
                    });
                    this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
                    this.cancel();
                }
            } else if (statusResponse.responseStatus === "EXCEPTION") {
                this.actionStatusCheckFailed();
                this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
            } else {
                this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                if (this.progressTimer) clearTimeout(this.progressTimer);
            }
        }, () => {
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
            if (this.progressTimer) clearTimeout(this.progressTimer);
        });
    }

    actionStatusCheckIntervalStart(statusId: any, type: string, validRecords: any) {
        this.pfs.actionStatusCheck(statusId).subscribe((statusResponse: any) => {
            if (statusResponse.responseStatus === "SUCCESS") {
                const response = statusResponse.responseData;
                const reqStatus = response.reqStatus.trim().toUpperCase();
                if (reqStatus === 'FAILED') {
                    if (response.failMsg && response.failMsg.toUpperCase() === 'STALEDATAEXCEPTION')
                        this.showStaleDataDialog();
                    else
                        this.actionStatusCheckFailed();

                    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                }

                if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
                    this.updateProgressBarStatus(response, type, validRecords);
                }
                if (reqStatus === 'COMPLETED') {
                    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                    if (this.progressTimer) clearTimeout(this.progressTimer);
                    this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                        severity: 'success',
                        summary: 'Import Orders',
                        detail: response.successRecCount + " orders imported/updated."
                    });
                    this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
                    this.cancel();
                }
            } else if (statusResponse.responseStatus === "EXCEPTION") {
                this.actionStatusCheckFailed();
                this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
            } else {
                this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                if (this.progressTimer) clearTimeout(this.progressTimer);
            }
        }, () => {
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
            if (this.progressTimer) clearTimeout(this.progressTimer);
        });
    }

    actionStatusCheckFailed() {
        this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        this.cancel();
        this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
            dType: 'ERROR',
            dTitle: '',
            dProceedBtnLabel: '',
            msg: 'An error occurred processing your request.',
            onProceed: () => {
            }
        } as IAlertDialogData);
    }

    showStaleDataDialog() {
        this.cancel();
        this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
            dTitle: 'Refresh Required',
            dProceedBtnLabel: 'Refresh',
            msg: "You are attempting to import orders which were modified by another user. Please refresh page to see latest changes.",
            onProceed: () => {
                this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
            }
        });
    }

    ngOnDestroy() {
        if (this.progressTimer) clearTimeout(this.progressTimer);
    }
}

